/**
 * Member Nutrition Page - Olympic Fit Wellness
 * Nutrition tracking and meal planning
 */
import { useState, useEffect } from 'react';
import { 
  Apple, 
  Flame, 
  Droplets,
  Plus,
  X,
  Loader2,
  Check,
  Clock
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';

export default function NutritionPage() {
  const { profile } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [meals, setMeals] = useState([]);
  const [stats, setStats] = useState({
    calories: 0,
    protein: 0,
    carbs: 0,
    fat: 0,
    meals_count: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [showLogModal, setShowLogModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const [mealForm, setMealForm] = useState({
    name: 'Breakfast',
    time: '',
    calories: 0,
    protein: 0,
    carbs: 0,
    fat: 0,
    items: '',
    notes: ''
  });

  // Daily goals (could be fetched from user profile in future)
  const dailyGoals = {
    calories: 2200,
    protein: 150,
    carbs: 250,
    fat: 70
  };

  useEffect(() => {
    loadData();
  }, [selectedDate]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [mealsData, statsData] = await Promise.all([
        api.getMyMeals(selectedDate),
        api.getNutritionStats(selectedDate)
      ]);
      setMeals(mealsData || []);
      setStats(statsData || { calories: 0, protein: 0, carbs: 0, fat: 0, meals_count: 0 });
    } catch (err) {
      console.error('Error loading nutrition data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogMeal = async (e) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const mealData = {
        name: mealForm.name,
        time: mealForm.time || new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
        calories: parseInt(mealForm.calories) || 0,
        protein: parseInt(mealForm.protein) || 0,
        carbs: parseInt(mealForm.carbs) || 0,
        fat: parseInt(mealForm.fat) || 0,
        items: mealForm.items.split(',').map(i => i.trim()).filter(i => i),
        notes: mealForm.notes
      };

      await api.logMeal(mealData);
      setSuccess('Meal logged successfully!');
      setShowLogModal(false);
      setMealForm({
        name: 'Breakfast',
        time: '',
        calories: 0,
        protein: 0,
        carbs: 0,
        fat: 0,
        items: '',
        notes: ''
      });
      loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message || 'Failed to log meal');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getProgress = (current, target) => Math.min((current / target) * 100, 100);

  const MacroCard = ({ label, current, target, unit, color }) => (
    <div className="bg-white rounded-xl p-4 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-neutral-500">{label}</span>
        <span className="text-sm font-medium">{current}/{target}{unit}</span>
      </div>
      <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
        <div 
          className={`h-full ${color} rounded-full transition-all`}
          style={{ width: `${getProgress(current, target)}%` }}
        />
      </div>
    </div>
  );

  const getMealIcon = (name) => {
    switch(name.toLowerCase()) {
      case 'breakfast': return '🌅';
      case 'lunch': return '☀️';
      case 'dinner': return '🌙';
      case 'snack': return '🍎';
      default: return '🍽️';
    }
  };

  return (
    <div className="space-y-6" data-testid="nutrition-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-neutral-900">Nutrition</h1>
          <p className="text-neutral-500">Track your daily intake and reach your goals</p>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="px-4 py-2 border border-neutral-200 rounded-lg text-sm"
          />
          <button
            onClick={() => setShowLogModal(true)}
            className="inline-flex items-center justify-center gap-2 bg-[#3B82F6] text-white font-semibold px-6 py-3 text-sm tracking-wider hover:bg-blue-600 transition-colors"
            data-testid="log-meal-btn"
          >
            <Plus className="w-4 h-4" />
            LOG MEAL
          </button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <Check className="w-5 h-5" />
          {success}
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <X className="w-5 h-5" />
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#3B82F6]" />
        </div>
      ) : (
        <>
          {/* Daily Summary */}
          <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold opacity-90">Today's Progress</h2>
                <p className="text-3xl font-bold">{stats.calories} / {dailyGoals.calories} cal</p>
              </div>
              <div className="text-right">
                <p className="text-sm opacity-80">{Math.max(0, dailyGoals.calories - stats.calories)} remaining</p>
                <p className="text-lg font-semibold">{Math.round(getProgress(stats.calories, dailyGoals.calories))}%</p>
              </div>
            </div>
            <div className="h-3 bg-white/30 rounded-full overflow-hidden">
              <div 
                className="h-full bg-white rounded-full transition-all"
                style={{ width: `${getProgress(stats.calories, dailyGoals.calories)}%` }}
              />
            </div>
          </div>

          {/* Macros Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MacroCard 
              label="Protein" 
              current={stats.protein} 
              target={dailyGoals.protein}
              unit="g"
              color="bg-red-500"
            />
            <MacroCard 
              label="Carbs" 
              current={stats.carbs} 
              target={dailyGoals.carbs}
              unit="g"
              color="bg-yellow-500"
            />
            <MacroCard 
              label="Fat" 
              current={stats.fat} 
              target={dailyGoals.fat}
              unit="g"
              color="bg-purple-500"
            />
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-neutral-500">Meals Today</span>
                <span className="text-sm font-medium">{stats.meals_count}</span>
              </div>
              <div className="text-2xl font-bold text-neutral-900">{meals.length} logged</div>
            </div>
          </div>

          {/* Meals Logged */}
          <div className="bg-white rounded-xl shadow-sm">
            <div className="flex items-center justify-between p-6 border-b border-neutral-100">
              <h2 className="text-xl font-heading font-bold text-neutral-900">Today's Meals</h2>
              <span className="text-sm text-neutral-500">{meals.length} meals logged</span>
            </div>
            
            {meals.length > 0 ? (
              <div className="divide-y divide-neutral-100">
                {meals.map((meal, idx) => (
                  <div key={meal.id || idx} className="p-4 flex items-start gap-4 hover:bg-neutral-50 transition-colors">
                    <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center text-2xl">
                      {getMealIcon(meal.name)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-neutral-900">{meal.name}</h3>
                        <span className="text-sm font-medium text-green-600">{meal.calories} cal</span>
                      </div>
                      <p className="text-sm text-neutral-500 flex items-center gap-1 mt-1">
                        <Clock className="w-3 h-3" />
                        {meal.time}
                      </p>
                      {meal.items && meal.items.length > 0 && (
                        <p className="text-sm text-neutral-400 mt-1">
                          {Array.isArray(meal.items) ? meal.items.join(', ') : meal.items}
                        </p>
                      )}
                      <div className="flex gap-4 mt-2 text-xs text-neutral-500">
                        <span>P: {meal.protein || 0}g</span>
                        <span>C: {meal.carbs || 0}g</span>
                        <span>F: {meal.fat || 0}g</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-12 text-center">
                <Apple className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
                <h3 className="font-semibold text-neutral-900 mb-2">No meals logged yet</h3>
                <p className="text-neutral-500 mb-4">Start tracking your nutrition!</p>
                <button
                  onClick={() => setShowLogModal(true)}
                  className="inline-flex items-center gap-2 bg-[#3B82F6] text-white font-semibold px-6 py-3 text-sm hover:bg-blue-600 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Log Your First Meal
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Log Meal Modal */}
      {showLogModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-neutral-100 flex items-center justify-between">
              <h2 className="text-xl font-heading font-bold">Log Meal</h2>
              <button onClick={() => setShowLogModal(false)} className="text-neutral-400 hover:text-neutral-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleLogMeal} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Meal Type</label>
                  <select
                    value={mealForm.name}
                    onChange={(e) => setMealForm({...mealForm, name: e.target.value})}
                    className="w-full px-4 py-2 border border-neutral-200 rounded-lg focus:outline-none focus:border-[#3B82F6]"
                  >
                    <option value="Breakfast">Breakfast</option>
                    <option value="Lunch">Lunch</option>
                    <option value="Dinner">Dinner</option>
                    <option value="Snack">Snack</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Time</label>
                  <input
                    type="time"
                    value={mealForm.time}
                    onChange={(e) => setMealForm({...mealForm, time: e.target.value})}
                    className="w-full px-4 py-2 border border-neutral-200 rounded-lg focus:outline-none focus:border-[#3B82F6]"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Food Items (comma separated)</label>
                <input
                  type="text"
                  value={mealForm.items}
                  onChange={(e) => setMealForm({...mealForm, items: e.target.value})}
                  className="w-full px-4 py-2 border border-neutral-200 rounded-lg focus:outline-none focus:border-[#3B82F6]"
                  placeholder="e.g., Oatmeal, Eggs, Toast"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Calories</label>
                  <input
                    type="number"
                    value={mealForm.calories}
                    onChange={(e) => setMealForm({...mealForm, calories: e.target.value})}
                    className="w-full px-4 py-2 border border-neutral-200 rounded-lg focus:outline-none focus:border-[#3B82F6]"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Protein (g)</label>
                  <input
                    type="number"
                    value={mealForm.protein}
                    onChange={(e) => setMealForm({...mealForm, protein: e.target.value})}
                    className="w-full px-4 py-2 border border-neutral-200 rounded-lg focus:outline-none focus:border-[#3B82F6]"
                    min="0"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Carbs (g)</label>
                  <input
                    type="number"
                    value={mealForm.carbs}
                    onChange={(e) => setMealForm({...mealForm, carbs: e.target.value})}
                    className="w-full px-4 py-2 border border-neutral-200 rounded-lg focus:outline-none focus:border-[#3B82F6]"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Fat (g)</label>
                  <input
                    type="number"
                    value={mealForm.fat}
                    onChange={(e) => setMealForm({...mealForm, fat: e.target.value})}
                    className="w-full px-4 py-2 border border-neutral-200 rounded-lg focus:outline-none focus:border-[#3B82F6]"
                    min="0"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Notes (optional)</label>
                <textarea
                  value={mealForm.notes}
                  onChange={(e) => setMealForm({...mealForm, notes: e.target.value})}
                  className="w-full px-4 py-2 border border-neutral-200 rounded-lg focus:outline-none focus:border-[#3B82F6] resize-none"
                  rows={2}
                  placeholder="Any additional notes..."
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowLogModal(false)}
                  className="flex-1 py-3 border border-neutral-200 rounded-lg font-semibold text-sm hover:bg-neutral-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 py-3 bg-[#3B82F6] text-white rounded-lg font-semibold text-sm hover:bg-blue-600 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {isSubmitting ? 'Saving...' : 'Log Meal'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
